<template>
  <div
    class="modal"
    :class="{ 'is-active': value }">

    <div class="modal-background"
      @click="close()">
    </div>
    
    <div class="modal-content">
      <div class="modal-card">

        <header class="modal-card-head">
          <p class="modal-card-title">Add a Product</p>
          <button
            class="delete"
            aria-label="close"
            @click="close(true)">
          </button>
        </header>

        <v-form class="modal-card-body" :valid.sync="valid">

          <div class="field">
            <label class="label">Product Brand*</label>
            <v-select 
              required
              :options="productBrands"
              v-model="product.product_brand"
            />
          </div>

          <div class="columns">
            <div class="column">
              <div class="label-and-button pb-2">
                <label class="label mb-0">Category*</label>
                <span 
                  class="tag pointer" 
                  @click="managingCategories = true">
                  <span class="icon is-small">
                    <i class="fas fa-plus"></i>
                  </span>
                  <span>Manage Categories</span>
                </span>
              </div>
              <v-select 
                required
                :options="categories"
                item-text="category"
                item-value="id"
                v-model="product.product_category_id"
              />
            </div>

            <div class="column" v-if="product.product_category_id">
              <div class="label-and-button pb-2">
                <label class="label mb-0">Subcategory*</label>
                <span 
                  class="tag pointer" 
                  @click="managingSubcategories = true">
                  <span class="icon is-small">
                    <i class="fas fa-plus"></i>
                  </span>
                  <span>Manage Subcategories</span>
                </span>
              </div>
              <v-select 
                v-if="subcategories.length > 0"
                required
                :options="subcategories"
                item-text="subcategory"
                item-value="id"
                v-model="product.product_subcategory_id"
              />
              <span v-else class="has-text-grey-light">
                No subcategories for this category
              </span>
            </div>
          </div>

          <div class="field">
            <label class="label">Product Name*</label>
            <v-text-field 
              required
              placeholder="Enter Product Name"
              v-model="product.name">
            </v-text-field>
          </div>

          <div class="field">
            <label class="label">Description / Specs</label>
            <v-text-field 
              placeholder="Enter Product Specs"
              v-model="product.desc">
            </v-text-field>
          </div>
          
          <div class="field">
            <label class="label">Product Unit*</label>
            <v-select 
              required
              :options="units"
              item-text="unit_display_name"
              item-value="id"
              v-model="product.unit_id"
            />
          </div>

          <div class="columns">
            <div class="column" v-if="!isMitrex">
              <label class="label">Product Unit Price*</label>
              <v-text-field 
                required
                placeholder="Enter Product Unit Price"
                type="number"
                v-model="product.unit_price">
              </v-text-field>
            </div>
            <div class="column">
              <label class="label">
                <span v-if="isMitrex">MPA Unit Price*</span>
                <span v-else>Unit Price with Installation*</span>
              </label>
              <v-text-field 
                required
                placeholder="Enter Unit Price"
                type="number"
                v-model="product.unit_price_w_install">
              </v-text-field>
            </div>
            <div class="column" v-if="isMitrex">
              <label class="label">
                DPA Unit Price*
              </label>
              <v-text-field 
                required
                placeholder="Enter Unit Price"
                type="number"
                v-model="product.unit_price_dpa">
              </v-text-field>
            </div>
          </div>

          <div class="columns is-multiline" 
            v-if="product.product_category_id && productCategory.has_dimensions">
            <div class="column pb-0 is-half" v-if="!isMitrex">
              <label class="label">Linear Unit Price</label>
              <v-text-field 
                required
                placeholder="Enter Product Linear Unit Price"
                type="number"
                v-model="product.linear_unit_price">
              </v-text-field>
            </div>
            <div class="column pb-0 is-half">
              <label class="label">
                <span v-if="isMitrex">MPA Linear Unit Price</span>
                <span v-else>Linear Unit Price with Installation</span>
              </label>
              <v-text-field 
                required
                placeholder="Enter Linear Unit Price"
                type="number"
                v-model="product.linear_unit_price_w_install">
              </v-text-field>
            </div>
            <div class="column pb-0 is-half" v-if="isMitrex">
              <label class="label">
                DPA Linear Unit Price*
              </label>
              <v-text-field 
                required
                placeholder="Enter Linear Unit Price"
                type="number"
                v-model="product.linear_unit_price_dpa">
              </v-text-field>
            </div>
            <div class="px-3 mb-2 help is-full">
              *Linear unit price is used to calculate prices of irregular cladding
            </div>
          </div>

          <template v-if="isMitrex">
            <div class="columns">
              <div class="column is-half">
                <label class="label">Railing Solar Capacity (Watts)</label>
                <v-text-field 
                  required
                  type="number"
                  placeholder="Solar Capacity"
                  v-model="product.capacity_w">
                </v-text-field>
              </div>
              <div class="column is-half">
                <label class="label">Cladding Cell Power</label>
                <v-text-field 
                  required
                  type="number"
                  placeholder="Cell Power"
                  v-model="product.cell_power">
                </v-text-field>
              </div>
            </div>
          </template>  

          <template v-if="product.product_category_id">  
            <template v-if="productCategory.has_dimensions">
              <hr>
              <div class="columns">
                <div class="column is-half">
                  <label class="label">Min. Length"</label>
                  <v-text-field 
                    placeholder="Min Product Length"
                    v-model="product.min_length_in_str">
                  </v-text-field>
                </div>
                <div class="column is-half">
                  <label class="label">Max. Length"</label>
                  <v-text-field 
                    placeholder="Min Product Length"
                    v-model="product.max_length_in_str">
                  </v-text-field>
                </div>
              </div>

              <div class="columns">
                <div class="column is-half">
                  <label class="label">Min. Width"</label>
                  <v-text-field
                    placeholder="Min Product Width"
                    v-model="product.min_width_in_str">
                  </v-text-field>
                </div>
                <div class="column is-half">
                  <label class="label">Max. Width"</label>
                  <v-text-field
                    placeholder="Max Product Width"
                    v-model="product.max_width_in_str">
                  </v-text-field>
                </div>
              </div>

              <div class="columns">
                <div class="column is-half">
                  <label class="label">Min. Thickness"</label>
                  <v-text-field
                    placeholder="Min Product Thickness"
                    v-model="product.min_thick_in_str">
                  </v-text-field>
                </div>
                <div class="column is-half">
                  <label class="label">Max. Thickness"</label>
                  <v-text-field
                    placeholder="Max Product Thickness"
                    v-model="product.max_thick_in_str">
                  </v-text-field>
                </div>
              </div>
              <hr>
            </template>
            <div v-else class="field">
              <label class="label">Product Size</label>
              <v-text-field 
                placeholder="Product Size"
                v-model="product.size">
              </v-text-field>
            </div>
          </template>

          <div class="field">
            <label class="label">Product Sample Image*</label>
            <v-file-upload
              class="is-fullwidth"
              has-name
              file-type-validator=".*"
              required
              v-model="imageUpload">
            </v-file-upload>
            <span class="help">
              *Not choosing a product image may affect the display of the 
              product in other parts of the application
            </span>
          </div>

        </v-form>

        <footer class="modal-card-foot">
          <button 
            :disabled="!valid || saving"
            class="button is-primary" 
            :class="{ 'is-loading': saving }"
            @click.stop="addNew()">
            Save
          </button>
          <button 
            class="button" 
            @click.stop="close(true)">
            Cancel
          </button>
        </footer>

      </div>
    </div>

    <manage-product-categories-modal 
      v-model="managingCategories"/>

    <manage-product-subcategories-modal 
      v-model="managingSubcategories"/>

  </div>
</template>

<script>
import VSelect from '@/components/base/VSelect'
import VTextField from '@/components/base/VTextField'
import VForm from '@/components/base/VForm'
import VFileUpload from '@/components/base/VFileUpload'

import ManageProductCategoriesModal from '@/components/inventory/ManageProductCategoriesModal'
import ManageProductSubcategoriesModal from '@/components/inventory/ManageProductSubcategoriesModal'

import { mapState, mapActions } from 'vuex'

export default {
  props: {
    value: {
      type: Boolean
    },
    productCategoryId: {
      type: [String, Number]
    }
  },
  components: { 
    VTextField, 
    VForm, 
    VSelect, 
    VFileUpload,
    ManageProductCategoriesModal,
    ManageProductSubcategoriesModal
  },
  data() {
    return {
      product: {
        product_brand: null,
        name: null,
        desc: null,
        product_category_id: null,
        is_discontinued: false,
        unit_id: null,
        unit_price: null,
        unit_price_w_install: null,
        linear_unit_price: null,
        linear_unit_price_w_install: null,
        unit_price_dpa: null,
        linear_unit_price_dpa: null,
        size: null,
        min_width_in_str: null,
        max_width_in_str: null,
        min_length_in_str: null,
        max_length_in_str: null,
        min_thick_in_str: null,
        max_thick_in_str: null,
        capacity_w: 5,
        cell_power: 5.2
      },
      imageUpload: {
        file: null,
        name: null
      },
      valid: false,
      managingCategories: false,
      managingSubcategories: false,
      saving: false
    }
  },
  computed: {
    ...mapState("products", ["productBrands", "categories", "subcategories"]),
    ...mapState("units", ["units"]),
    productCategory({ product }) {
      if (product.product_category_id) {
        return this.categories.filter(d => d.id == product.product_category_id)[0];
      } else {
        return null
      }
    },
    isMitrex({ product }) {
      if (this.product.product_brand && this.product.product_brand == 'Mitrex') {
        return true;
      } else {
        return false;
      }
    }
  },
  watch: {
    'product.product_category_id'(id) {
      this.getSubcategories({ product_category_id: id });
    },
    productCategoryId(id) {
      this.product.product_category_id = id;
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("products", [
      "getCategories", "getSubcategories", "addProduct", "addProductImage"
    ]),
    ...mapActions("units", ["getUnits"]),
    initialize() {
      if (this.categories.length == 0) {
        this.getCategories();
      }

      if (this.units.length == 0) {
        this.getUnits();
      }
    },
    addNew() {
      this.saving = true;
      this.addProduct({ 
        data: this.product,
        parameters: { 
          product_category_id: this.product.product_category_id,
          product_type_id: this.product.product_type_id 
        }
      }).then(resp => {
        if (this.imageUpload.file) {
          let formData = new FormData();
          formData.append(
            'image', this.imageUpload.file, this.imageUpload.name
          );

          this.addProductImage({
            productId: resp.data.id,
            data: formData
          }).then(() => {
            this.close(true);
          }).catch(() => this.saving = false);
        } else {
          this.close(true);
        }
      })
    },
    close(clear=false) {
      this.saving = false;
      this.$emit('input', false);
      if (clear) {
        this.clearData();
      }
    },
    clearData() {
      Object.assign(this.$data, this.$options.data.call(this));
    }
  }
}
</script>

<style lang="scss" scoped>
.label-and-button {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  .tag {
    cursor: pointer;
    &:hover {
      background: gainsboro;
    }
  }
}
</style>
